import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { ChevronDown as ChevronDownIcon } from 'lucide-react';
import React, { ChangeEvent, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from './Icon';
import { Icons } from './icons';
import { Loading } from './Loading';
import { TrivialTooltip } from './TrivialTooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';

function cn(...inputs: any[]) {
  return twMerge(clsx(inputs));
}

// Extended button variants with primary option
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs enabled:hover:bg-primary/90',
        primary:
          'bg-primary-600 text-white shadow-xs enabled:hover:bg-primary-500 focus:outline-none focus:border-primary-700 focus:ring-primary enabled:active:bg-primary-700',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        red: 'bg-red-600 text-white shadow-xs hover:bg-red-500 focus:outline-none focus:border-red-700 focus:ring-red active:bg-red-700',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        plain:
          'bg-white dark:bg-dark-gray-700 text-gray-700 dark:text-dark-gray-400 hover:text-gray-500 dark:hover:text-dark-gray-300 focus:outline-none focus:ring-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 dark:active:bg-dark-gray-700',
        plainWithBorder:
          'border border-gray-300 dark:border-dark-gray-900 bg-white dark:bg-dark-gray-800 text-gray-700 dark:text-dark-gray-400 hover:text-gray-500 dark:hover:text-dark-gray-300 focus:outline-none focus:ring-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 dark:active:bg-dark-gray-700',
        plainIcon:
          'bg-transparent text-gray-700 dark:text-dark-gray-400 hover:text-gray-500 dark:hover:text-dark-gray-300 focus:outline-none focus:ring-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 dark:active:bg-dark-gray-700',
        plainTransparent:
          'bg-transparent text-gray-700 dark:text-dark-gray-400 hover:text-gray-500 dark:hover:text-dark-gray-300 focus:outline-none focus:ring-blue focus:border-blue-300 active:text-gray-800 active:bg-gray-50 dark:active:bg-dark-gray-700',
        accent:
          'bg-accent-600 text-white shadow-xs hover:bg-accent-400 focus:outline-none focus:border-accent-700 focus:ring-accent active:bg-accent-700',
        link: 'text-primary underline-offset-4 hover:underline',
        redBorder:
          'border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/50',
        orangeBorder:
          'border border-orange-300 dark:border-orange-900 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900/50',
        blueBorder:
          'border border-blue-300 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/50',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export enum ButtonColors {
  primary = 'primary',
  red = 'red',
  plain = 'plain',
  plainWithBorder = 'plainWithBorder',
  plainIcon = 'plainIcon',
  plainTransparent = 'plainTransparent',
  accent = 'accent',
  redBorder = 'redBorder',
  orangeBorder = 'orangeBorder',
  blueBorder = 'blueBorder',
  link = 'link',
}

type tooltipOptions = {
  text?: string;
  iconColorClassName?: string;
  type?: string;
  iconSizeClassName?: string;
  iconClassName?: string;
  tipClassName?: string;
  containerClassName?: string;
  svgClassName?: string;
  id?: string;
  iconStyle?: any;
  nullOnEmpty?: boolean;
};

type Props = {
  ref?: React.RefObject<HTMLButtonElement>;
  text?: string;
  label?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  loading?: boolean;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  fullWidth?: boolean;
  fullHeight?: boolean;
  color?: ButtonColors;
  icon?: React.ReactNode | Icons;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  extraOptions?: { text: string; onClick: (e?: any) => void }[];
  tooltip?: string;
  fileUpload?: boolean;
  onSelectFile?: (e: ChangeEvent<HTMLInputElement>) => void;
  badge?: number | string;
  autoFocus?: boolean;
  tooltipIconClassName?: string;
  trivialTooltip?: tooltipOptions;
  omitWhenDisabled?: boolean;
  visible?: boolean;
  acceptTypes?: string[];
  multiple?: boolean;
};

const defaultTrivialTooltipOptions: tooltipOptions = {
  tipClassName: 'text-left w-56',
  iconColorClassName: 'text-white',
  type: 'warn',
  iconSizeClassName: 'h-4 w-4',
};

// Map ButtonColors to shadcn variants
const colorToVariant = (color: ButtonColors) => {
  switch (color) {
    case ButtonColors.primary:
      return 'primary';
    case ButtonColors.red:
      return 'red';
    case ButtonColors.plain:
      return 'plain';
    case ButtonColors.plainWithBorder:
      return 'plainWithBorder';
    case ButtonColors.plainIcon:
      return 'plainIcon';
    case ButtonColors.plainTransparent:
      return 'plainTransparent';
    case ButtonColors.accent:
      return 'accent';
    case ButtonColors.redBorder:
      return 'redBorder';
    case ButtonColors.orangeBorder:
      return 'orangeBorder';
    case ButtonColors.blueBorder:
      return 'blueBorder';
    case ButtonColors.link:
      return 'link';
    default:
      return 'primary';
  }
};

export const Button: React.FC<Props> = ({
  ref,
  text,
  label,
  onClick,
  disabled,
  loading,
  size = 'default',
  fullWidth,
  fullHeight,
  color = ButtonColors.primary,
  icon,
  className,
  type = 'button',
  extraOptions,
  tooltip,
  fileUpload,
  onSelectFile,
  badge,
  autoFocus,
  tooltipIconClassName = 'h-3 w-3 text-white inline-block ml-2 align-text-top',
  trivialTooltip,
  omitWhenDisabled,
  visible = true,
  acceptTypes,
  multiple,
}) => {
  text = text || label;
  const activeTrivialTooltipOptions = {
    ...defaultTrivialTooltipOptions,
    ...trivialTooltip,
  };

  const fileInput = useRef<HTMLInputElement | null>(null);
  const variant = colorToVariant(color);
  const iconElement = React.isValidElement<{ className?: string }>(icon) ? icon : undefined;
  const iconContent =
    typeof icon === 'string' ? (
      <Icon icon={icon as Icons} className="w-5 h-5" />
    ) : iconElement ? (
      React.cloneElement(iconElement, {
        className: cn('w-5 h-5', iconElement.props.className),
      })
    ) : (
      icon
    );

  if (!visible) {
    return null;
  }

  const onClickStep = (e: any) => {
    if (fileUpload) {
      fileInput.current?.click();
    } else {
      onClick?.(e);
    }
  };

  if (omitWhenDisabled && disabled) {
    return null;
  }

  const baseButtonClasses = cn(
    buttonVariants({ variant: variant as any, size }),
    {
      'w-full': fullWidth,
      'h-full': fullHeight,
      'cursor-not-allowed opacity-50': disabled && !loading,
    },
    className
  );

  const hasExtraOptions = !!extraOptions?.length;

  return hasExtraOptions && !onClick ? (
    <DropdownMenu>
      <div className={cn('-ml-px relative block', fullWidth && 'w-full', fullHeight && 'h-full')}>
        <DropdownMenuTrigger asChild>
          <button
            type={type}
            aria-label={text || label || tooltip}
            aria-busy={loading || undefined}
            className={cn(
              baseButtonClasses,
              'justify-center items-center py-2 pl-4 pr-2 rounded-md'
            )}
            disabled={disabled || loading}
          >
            {loading && <Loading className="-ml-1 mr-3 w-4 h-4" />}
            {text}
            <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-56" align="end">
          <ScrollArea className="max-h-96">
            {extraOptions.map((opt, i) => (
              <DropdownMenuItem
                key={i}
                className="cursor-pointer"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  opt.onClick();
                }}
              >
                {opt.text}
              </DropdownMenuItem>
            ))}
          </ScrollArea>
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  ) : (
    <label
      className={cn(
        'relative inline-flex',
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        icon && !text && 'h-5'
      )}
      htmlFor={fileUpload ? 'file-upload' : label ? label : undefined}
    >
      <button
        autoFocus={autoFocus}
        type={type}
        aria-label={text || label || tooltip}
        aria-busy={loading || undefined}
        onClick={onClickStep}
        className={cn(
          baseButtonClasses,
          'justify-center items-center relative',
          extraOptions?.length ? 'rounded-r-none' : ''
        )}
        disabled={disabled || loading}
      >
        {!!badge && (
          <span className="absolute inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-black bg-white border-2 border-gray-300 rounded-full -top-2 -right-2 dark:border-gray-900">
            {badge}
          </span>
        )}
        {loading ? (
          <Loading color="text-gray-500 dark:text-dark-gray-400" className="-ml-1 mr-3" size={5} />
        ) : (
          iconContent
        )}
        {text}
        {!!tooltip && (
          <TrivialTooltip
            text={tooltip}
            iconClassName={cn(
              tooltipIconClassName,
              (color === ButtonColors.plain || color === ButtonColors.plainWithBorder) &&
                'text-gray-500'
            )}
          />
        )}
        <TrivialTooltip {...activeTrivialTooltipOptions} />
      </button>
      {hasExtraOptions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                baseButtonClasses,
                'h-9 relative items-center px-2 py-2 rounded-r-md text-sm font-medium focus:z-10 focus:outline-none focus:ring-1',
                color === ButtonColors.primary
                  ? 'border-r-0 border-t-0 border-b-0 border-l border-white'
                  : 'border border-gray-300',
                'rounded-l-none'
              )}
              disabled={disabled || loading}
            >
              <span className="sr-only">Open options</span>
              <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <ScrollArea className="h-96">
              {extraOptions.map((opt, i) => (
                <DropdownMenuItem
                  key={i}
                  className="cursor-pointer"
                  onClick={() => {
                    opt.onClick();
                  }}
                >
                  {opt.text}
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {fileUpload && (
        <input
          ref={fileInput}
          id="file-upload"
          name="file-upload"
          type="file"
          multiple={multiple}
          className="sr-only"
          onChange={onSelectFile}
          accept={acceptTypes?.join(',')}
        />
      )}
    </label>
  );
};
